import { useApolloClient } from "@apollo/client";
import { assert } from "@msys/common";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import { Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../../commons/modals/ConfirmProcess.js";
import { useTemplateUpdateProtocol } from "../useTemplateUpdateProtocol.js";
import { useSubscribeToQuoteTemplateMutation } from "./QuoteTemplateSubscribeButton.generated.js";
import { QuoteTemplateUpdateSubscribedVersionButton_QuoteTemplateFragment } from "./QuoteTemplateUpdateSubscribedVersionButton.generated.js";

interface Props {
  doc: QuoteTemplateUpdateSubscribedVersionButton_QuoteTemplateFragment;
  pathToDoc: string;
  onAfterUpdate?: (opts: {
    docId: string;
    toVersionNumber: number;
  }) => void | Promise<void>;
  onBeforeUpdate?: (opts: {
    docId: string;
    toVersionNumber: number;
  }) => void | Promise<void>;
}
export function QuoteTemplateUpdateSubscribedVersionButton({
  doc,
  pathToDoc,
  onBeforeUpdate,
  onAfterUpdate,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslate(["Templates", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const hasOverridesDefined =
    useTemplateUpdateProtocol(doc.id)?.hasOverridesDefined ?? false;

  const client = useApolloClient();
  const [subscribe, { loading }] = useSubscribeToQuoteTemplateMutation({
    client,
  });

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const handleUpdate = async () => {
    try {
      const latestSharedVersionNumber =
        doc.templateSharing?.latestSharedVersionNumber;
      assert(
        latestSharedVersionNumber,
        "expected doc.templateSharing.latestSharedVersionNumber"
      );

      const r = await startConfirmProcess(
        hasOverridesDefined
          ? {
              title: t("Upgrade subscribed template to the latest version?", {
                ns: "Templates",
              }),
              text: t(
                "You are upgrading to the latest version of the template. If any of your template configurations are not be compatible with the new version, they will be deleted. Do you want to proceed?",
                {
                  ns: "Templates",
                }
              ),
              notInStack: true,
            }
          : {
              title: t("Upgrade subscribed template to the latest version?", {
                ns: "Templates",
              }),
              notInStack: true,
            }
      );
      if (!r) return;

      const result = await subscribe({
        variables: {
          input: {
            quoteTemplateId: doc.id,
            versionNumber: latestSharedVersionNumber,
            applyOverrides: true,
          },
        },
      });
      const subscribedVersion =
        result.data?.subscribeToQuoteTemplate.quoteTemplate.templateSharing
          ?.subscribedToTemplate?.subscribedVersionNumber;
      if (!subscribedVersion) throw new Error("Subscribed version missing");

      await onBeforeUpdate?.({
        docId: doc.id,
        toVersionNumber: latestSharedVersionNumber,
      });

      navigate(`${pathToDoc}/${subscribedVersion}`);
      enqueueSnackbar(
        t("Subscribed template version updated", { ns: "Templates" })
      );

      await client.reFetchObservableQueries();
      await onAfterUpdate?.({
        docId: doc.id,
        toVersionNumber: latestSharedVersionNumber,
      });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
      Sentry.captureException(e);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="warning"
        onClick={handleUpdate}
        loading={loading}
        startIcon={<UpgradeIcon />}
      >
        {t("Update to this version", { ns: "Templates" })}
      </Button>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
}
